import { useState, useEffect } from 'react';
import { Sparkles, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const STORAGE_KEY = 'whatChanged.nonDivExpansion.dismissed.v1';

/**
 * WhatChangedBanner — Round 95z+ (Jun 2026).
 *
 * One-time, admin-only floating banner that introduces the
 * Non-Dividend Stock Expansion. Walks through the two new 3-way
 * segmented controls (Top Movers + Stock Table), reminds the admin
 * the controls track independently, and explains what each mode
 * surfaces. Persists dismissal in localStorage so it appears once
 * per browser per admin account.
 *
 * The banner is intentionally small and floats in the lower-right
 * to avoid disrupting the dashboard layout. Click X to dismiss.
 *
 * Why localStorage instead of a backend flag? Per-browser dismissal
 * is the right UX — if the admin switches devices we want a fresh
 * reminder. Lower complexity than a Mongo round-trip too.
 */
export default function WhatChangedBanner() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') return;
    } catch { /* private mode — show anyway */ }
    setOpen(true);
  }, [user]);

  if (!open) return null;

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch { /* private mode — banner won't reopen this session anyway */ }
  };

  return (
    <div
      className="fixed bottom-20 right-4 z-40 w-full max-w-sm rounded-lg border border-fuchsia-500/50 bg-card shadow-2xl shadow-fuchsia-500/10"
      role="alertdialog"
      aria-labelledby="what-changed-title"
      data-testid="what-changed-banner"
    >
      <div className="flex items-start gap-3 p-4">
        <Sparkles className="w-4 h-4 mt-0.5 text-fuchsia-400 shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <h3 id="what-changed-title" className="text-sm font-bold text-foreground">
            New: Non-Dividend Expansion
          </h3>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Two new 3-way pill controls let you flip between{' '}
            <span className="text-foreground font-semibold">Dividend</span> /{' '}
            <span className="text-foreground font-semibold">All</span> /{' '}
            <span className="text-foreground font-semibold">Non-Dividend</span>:
          </p>
          <ul className="text-[11px] text-muted-foreground leading-relaxed space-y-1 pl-3 list-disc marker:text-fuchsia-400">
            <li>One on the <span className="text-foreground font-semibold">Top Movers</span> widget</li>
            <li>One on the <span className="text-foreground font-semibold">Stock Table</span> bottom row</li>
          </ul>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Both track independently and default to Dividend. Non-div rows show a quality tier pill
            (<span className="text-emerald-300 font-mono text-[10px]">G-S</span> →{' '}
            <span className="text-rose-300 font-mono text-[10px]">SPEC</span>) in the new Tier column.
            Locked pills are admin-only.
          </p>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="what-changed-banner-dismiss"
          className="shrink-0 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/30"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
